import React, { useState, useEffect } from 'react';
import { Search, TrendingUp, Calendar, ExternalLink, RefreshCw, Newspaper, Filter, ChevronDown } from 'lucide-react';

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(to bottom right, #0f172a, #1e293b, #0f172a)',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  header: {
    background: 'linear-gradient(to right, #2563eb, #4f46e5)',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
    position: 'sticky',
    top: 0,
    zIndex: 50,
  },
  headerContent: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '24px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logo: {
    width: '40px',
    height: '40px',
    background: 'white',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: 'white',
  },
  subtitle: {
    fontSize: '14px',
    color: '#e0e7ff',
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'white',
    color: '#2563eb',
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'all 0.3s',
  },
  main: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '32px 16px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '16px',
    marginBottom: '32px',
  },
  statCard: {
    background: 'linear-gradient(to bottom right, #3b82f6, #1e40af)',
    borderRadius: '8px',
    padding: '24px',
    color: 'white',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statCard2: {
    background: 'linear-gradient(to bottom right, #4f46e5, #2d1b69)',
  },
  statCard3: {
    background: 'linear-gradient(to bottom right, #a855f7, #6b21a8)',
  },
  statLabel: {
    fontSize: '14px',
    opacity: 0.8,
    marginBottom: '8px',
  },
  statNumber: {
    fontSize: '28px',
    fontWeight: 'bold',
  },
  filterSection: {
    background: '#1e293b',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '32px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)',
  },
  searchContainer: {
    position: 'relative',
    marginBottom: '16px',
  },
  searchInput: {
    width: '100%',
    paddingLeft: '40px',
    paddingRight: '16px',
    paddingTop: '12px',
    paddingBottom: '12px',
    background: '#334155',
    border: '1px solid #475569',
    borderRadius: '8px',
    color: 'white',
    fontSize: '16px',
  },
  categoryButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  categoryBtn: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'all 0.3s',
    background: '#475569',
    color: '#cbd5e1',
  },
  categoryBtnActive: {
    background: '#2563eb',
    color: 'white',
    boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.4)',
  },
  briefingsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  briefingCard: {
    background: '#1e293b',
    borderRadius: '8px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)',
    overflow: 'hidden',
    border: '1px solid #334155',
    transition: 'all 0.3s',
  },
  briefingCardHover: {
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
    borderColor: '#3b82f6',
  },
  briefingHeader: {
    padding: '24px 24px 16px',
  },
  badges: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  categoryBadge: {
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: '600',
  },
  importanceBadge: {
    fontSize: '12px',
    fontWeight: 'bold',
  },
  briefingTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: 'white',
    marginBottom: '8px',
    lineHeight: '1.5',
  },
  briefingMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    fontSize: '14px',
    color: '#94a3b8',
  },
  briefingText: {
    padding: '0 24px 16px',
    color: '#cbd5e1',
    lineHeight: '1.6',
    whiteSpace: 'pre-line',
  },
  expandButton: {
    width: '100%',
    padding: '12px 24px',
    background: '#475569',
    color: '#60a5fa',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'all 0.3s',
    borderTop: '1px solid #334155',
  },
  expandButtonHover: {
    background: '#475569',
  },
  expandedContent: {
    padding: '16px 24px',
    background: '#0f4c75cc',
    borderTop: '1px solid #334155',
  },
  sourcesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  sourceItem: {
    padding: '12px',
    background: '#1e293b',
    borderRadius: '6px',
    border: '1px solid #475569',
    textDecoration: 'none',
    color: 'inherit',
    cursor: 'pointer',
    transition: 'all 0.3s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sourceItemHover: {
    background: '#334155',
    borderColor: '#3b82f6',
  },
  sourceTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#e2e8f0',
    flex: 1,
    marginRight: '12px',
  },
  sourceSource: {
    fontSize: '12px',
    color: '#94a3b8',
  },
  footer: {
    background: '#0f172a',
    borderTop: '1px solid #334155',
    padding: '32px 16px',
    marginTop: '48px',
  },
  footerText: {
    textAlign: 'center',
    color: '#64748b',
    fontSize: '14px',
    maxWidth: '1280px',
    margin: '0 auto',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 16px',
    color: '#cbd5e1',
  },
  error: {
    background: 'rgba(153, 27, 27, 0.3)',
    border: '1px solid #dc2626',
    borderRadius: '8px',
    padding: '16px',
    color: '#fca5a5',
    marginBottom: '32px',
  },
  categoryColors: {
    'Economy': { bg: '#dbeafe', text: '#1e3a8a' },
    'Politics': { bg: '#fee2e2', text: '#7f1d1d' },
    'Business': { bg: '#dcfce7', text: '#15803d' },
    'Technology': { bg: '#e9d5ff', text: '#581c87' },
    'Health': { bg: '#fce7f3', text: '#831843' },
    'Entertainment': { bg: '#fef3c7', text: '#78350f' },
    'Geopolitics': { bg: '#e0e7ff', text: '#3730a3' },
    'World Affairs': { bg: '#fed7aa', text: '#7c2d12' },
    'Environment': { bg: '#ccfbf1', text: '#134e4a' },
  },
};

export default function BharatBrief() {
  const [briefings, setBriefings] = useState([]);
  const [filteredBriefings, setFilteredBriefings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [expandedId, setExpandedId] = useState(null);

  const API_URL = 'https://bharat-brief.bharatbrief.workers.dev';

  useEffect(() => {
    fetchBriefings();
  }, []);

  const fetchBriefings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/api/briefings`);
      
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      
      const data = await response.json();
      setBriefings(data);
      setFilteredBriefings(data);
    } catch (err) {
      setError(`Failed to load briefings: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = briefings;

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(b => b.category === selectedCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(b =>
        b.topic.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.briefing.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredBriefings(filtered);
  }, [searchQuery, selectedCategory, briefings]);

  const categories = ['All', 'Economy', 'Politics', 'Business', 'Technology', 'Health', 'Entertainment', 'Geopolitics', 'World Affairs', 'Environment'];

  const getCategoryColor = (category) => styles.categoryColors[category] || { bg: '#e5e7eb', text: '#374151' };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            <div style={styles.logo}>
              <Newspaper size={24} color="#2563eb" />
            </div>
            <div>
              <div style={styles.title}>Bharat Brief</div>
              <div style={styles.subtitle}>AI-Powered News Intelligence</div>
            </div>
          </div>
          <button style={styles.refreshBtn} onClick={fetchBriefings}>
            <RefreshCw size={20} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
            <span>Refresh</span>
          </button>
        </div>
      </header>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Main Content */}
      <main style={styles.main}>
        {/* Stats */}
        <div style={styles.statsGrid}>
          <div style={{ ...styles.statCard }}>
            <div>
              <div style={styles.statLabel}>Total Briefings</div>
              <div style={styles.statNumber}>{briefings.length}</div>
            </div>
            <Newspaper size={48} style={{ opacity: 0.2 }} />
          </div>
          <div style={{ ...styles.statCard, ...styles.statCard2 }}>
            <div>
              <div style={styles.statLabel}>Categories</div>
              <div style={styles.statNumber}>{new Set(briefings.map(b => b.category)).size}</div>
            </div>
            <Filter size={48} style={{ opacity: 0.2 }} />
          </div>
          <div style={{ ...styles.statCard, ...styles.statCard3 }}>
            <div>
              <div style={styles.statLabel}>Sources Used</div>
              <div style={styles.statNumber}>{briefings.reduce((sum, b) => sum + b.sourceCount, 0)}</div>
            </div>
            <TrendingUp size={48} style={{ opacity: 0.2 }} />
          </div>
        </div>

        {/* Search & Filter */}
        <div style={styles.filterSection}>
          <div style={styles.searchContainer}>
            <Search size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              type="text"
              placeholder="Search briefings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />
          </div>
          <div style={styles.categoryButtons}>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                style={{
                  ...styles.categoryBtn,
                  ...(selectedCategory === cat ? styles.categoryBtnActive : {}),
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Loading */}
        {loading && <div style={styles.loading}><RefreshCw size={48} style={{ marginBottom: '16px', animation: 'spin 1s linear infinite', color: '#3b82f6' }} />Loading briefings...</div>}

        {/* Error */}
        {error && <div style={styles.error}><strong>Error:</strong> {error}</div>}

        {/* No Results */}
        {!loading && !error && filteredBriefings.length === 0 && <div style={{ textAlign: 'center', padding: '48px 16px', color: '#94a3b8' }}>No briefings found</div>}

        {/* Briefings */}
        {!loading && !error && filteredBriefings.length > 0 && (
          <div style={styles.briefingsContainer}>
            {filteredBriefings.map((briefing) => {
              const colors = getCategoryColor(briefing.category);
              return (
                <div key={briefing.id} style={styles.briefingCard}>
                  <div style={styles.briefingHeader}>
                    <div style={styles.badges}>
                      <span style={{ ...styles.categoryBadge, background: colors.bg, color: colors.text }}>{briefing.category}</span>
                      <span style={{ ...styles.importanceBadge, color: briefing.importance === 'high' ? '#dc2626' : '#ca8a04' }}>
                        {briefing.importance === 'high' ? '🔴 HIGH' : '⚠️ STANDARD'}
                      </span>
                    </div>
                    <div style={styles.briefingTitle}>{briefing.topic}</div>
                    <div style={styles.briefingMeta}>
                      <span>📅 {new Date(briefing.synthesizedAt).toLocaleDateString('en-IN')}</span>
                      <span>{briefing.sourceCount} sources</span>
                    </div>
                  </div>
                  <div style={styles.briefingText}>{briefing.briefing}</div>
                  <button
                    style={styles.expandButton}
                    onClick={() => setExpandedId(expandedId === briefing.id ? null : briefing.id)}
                  >
                    <span>{expandedId === briefing.id ? 'Hide' : 'View'} Sources & Details</span>
                    <ChevronDown size={20} style={{ transform: expandedId === briefing.id ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
                  </button>
                  {expandedId === briefing.id && (
                    <div style={styles.expandedContent}>
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#cbd5e1', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Newspaper size={16} /> Original Sources ({briefing.sources?.length || 0})
                        </div>
                        <div style={styles.sourcesList}>
                          {briefing.sources?.map((source, idx) => (
                            <a
                              key={idx}
                              href={source.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ ...styles.sourceItem, textDecoration: 'none' }}
                              onMouseEnter={(e) => Object.assign(e.target.style, styles.sourceItemHover)}
                              onMouseLeave={(e) => Object.assign(e.target.style, styles.sourceItem)}
                            >
                              <div>
                                <div style={styles.sourceTitle}>{source.title}</div>
                                <div style={styles.sourceSource}>{source.source}</div>
                              </div>
                              <ExternalLink size={16} color="#94a3b8" />
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerText}>
          Powered by <span style={{ color: '#60a5fa', fontWeight: '500' }}>NewsAPI</span> + <span style={{ color: '#60a5fa', fontWeight: '500' }}>Google Gemini AI</span> • <span style={{ color: '#4ade80', fontWeight: '500' }}>100% Legal</span> • <span style={{ color: '#a78bfa', fontWeight: '500' }}>Open Source</span>
        </div>
      </footer>
    </div>
  );
}